import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileSignature, Plus, Pencil, Trash2, Eye, Copy, Mail, Download, X,
  CheckCircle2, Clock, Send, PenLine, RefreshCcw,
} from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { SignaturePad } from "../components/SignaturePad";
// Server builds the signed-agreement HTML on its own; the client no longer
// imports buildAgreementHtml. (The file still exists for the deprecated
// client-side rendering path we may remove later.)

const fmtDMY = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : s;
};
const fmtGBP = (n) =>
  n === null || n === undefined || n === "" ? "" : `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const AgreementStatusPill = ({ status, size = "sm" }) => {
  const map = {
    draft: { label: "Draft", cls: "bg-cream-3 text-forest/70 border-forest/15", Icon: PenLine },
    sent: { label: "Sent to Client", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: Send },
    signed: { label: "Signed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  };
  const { label, cls, Icon } = map[status] || map.draft;
  const sz = size === "xs"
    ? "text-[9.5px] px-1.5 py-0.5"
    : "text-[10.5px] px-2.5 py-1";
  return (
    <span className={`inline-flex items-center gap-1.5 ${sz} font-bold tracking-[0.14em] uppercase border rounded ${cls}`}>
      <Icon size={size === "xs" ? 9 : 11} strokeWidth={1.8} /> {label}
    </span>
  );
};

export const CustomerAgreements = ({ customerId, customer }) => {
  const [list, setList] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null); // agreement object or "new"
  const [signing, setSigning] = useState(null); // agreement for in-person signing
  const [linkFor, setLinkFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/customers/${customerId}/service-agreements`,
        authHeaders()
      );
      setList(data);
      setLoadError(null);
    } catch (e) {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load agreements");
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const { data } = await axios.post(
        `${API}/admin/customers/${customerId}/service-agreements`,
        { product_type: customer?.case_type || "" },
        authHeaders()
      );
      await load();
      setEditing(data);
      toast.success("Draft created — tick the fields the client needs to see.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create");
    }
  };

  const remove = async (a) => {
    if (!window.confirm("Delete this draft agreement?")) return;
    try {
      await axios.delete(`${API}/admin/service-agreements/${a.id}`, authHeaders());
      toast.success("Agreement deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete");
    }
  };

  const sendLink = async (a) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/service-agreements/${a.id}/send`,
        {},
        authHeaders()
      );
      await load();
      setLinkFor({ ...a, token: data.token });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create link");
    }
  };

  const [emailFor, setEmailFor] = useState(null);

  if (list === null && !loadError) {
    return <p className="text-sm text-sage py-8 text-center">Loading…</p>;
  }

  return (
    <div className="space-y-5" data-testid="customer-agreements-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-serif text-lg text-forest">Service Agreements</h3>
          <p className="text-[12.5px] text-sage mt-1">Follows the firm's master Service Agreement wording. One per product.</p>
        </div>
        <button
          type="button"
          onClick={create}
          data-testid="create-service-agreement-btn"
          className="btn-gold inline-flex items-center gap-2"
        >
          <Plus size={14} strokeWidth={2} /> Create Service Agreement
        </button>
      </div>

      {loadError && <p className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3">{loadError}</p>}

      {list && list.length === 0 ? (
        <div className="bg-cream-3 border border-forest/10 px-6 py-10 text-center">
          <FileSignature className="mx-auto text-forest/40 mb-3" size={28} strokeWidth={1.2} />
          <p className="text-[13px] text-sage">No service agreements yet. Click "Create Service Agreement" to draft one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((a) => (
            <AgreementRow
              key={a.id}
              a={a}
              onEdit={() => setEditing(a)}
              onDelete={() => remove(a)}
              onCreateLink={() => sendLink(a)}
              onEmailClient={() => setEmailFor(a)}
              onSignNow={() => setSigning(a)}
              onOpenLink={() => setLinkFor(a)}
              onRefresh={load}
            />
          ))}
        </div>
      )}

      {editing && (
        <AgreementEditor
          agreement={editing}
          customer={customer}
          onClose={() => setEditing(null)}
          onSaved={async () => { await load(); setEditing(null); }}
        />
      )}
      {signing && (
        <AgreementSignNow
          agreement={signing}
          customer={customer}
          onClose={() => setSigning(null)}
          onSigned={async () => { await load(); setSigning(null); }}
        />
      )}
      {linkFor && (
        <AgreementLinkModal agreement={linkFor} onClose={() => setLinkFor(null)} />
      )}
      {emailFor && (
        <AgreementEmailModal
          agreement={emailFor}
          onClose={() => setEmailFor(null)}
          onSent={async () => { await load(); setEmailFor(null); }}
        />
      )}
    </div>
  );
};

const AgreementRow = ({ a, onEdit, onDelete, onCreateLink, onEmailClient, onSignNow, onOpenLink, onRefresh }) => {
  const canEdit = a.status !== "signed";
  return (
    <div
      data-testid={`agreement-row-${a.id}`}
      className="bg-white border border-forest/10 px-4 py-3 sm:px-5 sm:py-4"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-serif text-[16px] text-forest">
              Service Agreement
              {a.product_type ? <span className="text-forest/60 font-normal"> · {a.product_type.replace(/_/g, " ")}</span> : ""}
            </p>
            <AgreementStatusPill status={a.status} />
          </div>
          <p className="text-[12px] text-sage mt-1">
            Dated {a.agreement_date_dmy || "—"}
            {a.status === "signed" && a.signed_at_dmy && (
              <> · Signed on <strong className="text-forest">{a.signed_at_dmy}</strong></>
            )}
            {a.property_price ? <> · {fmtGBP(a.property_price)} property</> : ""}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {canEdit && (
            <button type="button" onClick={onEdit} data-testid={`agreement-edit-${a.id}`} className="p-2 text-forest/70 hover:text-forest" title="Edit draft"><Pencil size={16} /></button>
          )}
          {canEdit && a.status === "draft" && (
            <>
              <button type="button" onClick={onEmailClient} data-testid={`agreement-email-${a.id}`} className="btn-gold inline-flex items-center gap-1.5 !py-1.5 !px-3" title="Send the private link to the customer's email with a short branded cover note">
                <Send size={13} /> Email to Client
              </button>
              <button type="button" onClick={onCreateLink} data-testid={`agreement-send-${a.id}`} className="btn-ghost-dark inline-flex items-center gap-1.5 !py-1.5 !px-3" title="Just create a private link I can share myself">
                <Mail size={13} /> Private Link
              </button>
            </>
          )}
          {canEdit && a.status === "sent" && (
            <button type="button" onClick={onEmailClient} data-testid={`agreement-resend-email-${a.id}`} className="btn-ghost-dark inline-flex items-center gap-1.5 !py-1.5 !px-3" title="Resend the email">
              <Send size={13} /> Resend Email
            </button>
          )}
          {canEdit && (
            <button type="button" onClick={onSignNow} data-testid={`agreement-sign-now-${a.id}`} className="btn-gold inline-flex items-center gap-1.5 !py-1.5 !px-3" title="Open the agreement to sign with the client in front of you">
              <PenLine size={13} /> Sign With Client Now
            </button>
          )}
          {a.status === "sent" && (
            <button type="button" onClick={onOpenLink} className="p-2 text-forest/70 hover:text-forest" title="View client link"><Mail size={16} /></button>
          )}
          {a.status === "signed" && a.attached_document_id && (
            <>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const resp = await axios.get(
                      `${API}/admin/customers/${a.customer_id}/documents/${a.attached_document_id}/file`,
                      { ...authHeaders(), responseType: "blob" }
                    );
                    const url = URL.createObjectURL(resp.data);
                    window.open(url, "_blank", "noopener");
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                  } catch {
                    toast.error("Could not open the signed agreement");
                  }
                }}
                className="p-2 text-forest/70 hover:text-forest"
                data-testid={`agreement-view-${a.id}`}
                title="View signed PDF"
              >
                <Eye size={16} />
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const resp = await axios.get(
                      `${API}/admin/customers/${a.customer_id}/documents/${a.attached_document_id}/file?download=true`,
                      { ...authHeaders(), responseType: "blob" }
                    );
                    const url = URL.createObjectURL(resp.data);
                    const el = document.createElement("a");
                    el.href = url;
                    el.download = `Service Agreement.pdf`;
                    document.body.appendChild(el); el.click(); el.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                  } catch {
                    toast.error("Could not download");
                  }
                }}
                className="p-2 text-forest/70 hover:text-forest"
                data-testid={`agreement-download-${a.id}`}
                title="Download signed PDF"
              >
                <Download size={16} />
              </button>
            </>
          )}
          {canEdit && (
            <button type="button" onClick={onDelete} data-testid={`agreement-delete-${a.id}`} className="p-2 text-forest/60 hover:text-red-600" title="Delete"><Trash2 size={16} /></button>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, mark, children, hint }) => (
  <div className="space-y-1.5">
    <label className="flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">
      {label}
      {mark && (
        <span className="text-[9px] tracking-[0.16em] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
          Needs Review
        </span>
      )}
    </label>
    {children}
    {hint && <p className="text-[11.5px] text-sage">{hint}</p>}
  </div>
);

const AgreementEditor = ({ agreement, customer, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({
    services_description: agreement.services_description || "",
    property_price: agreement.property_price ?? "",
    deposit_amount: agreement.deposit_amount ?? "",
    service_fee_percent: agreement.service_fee_percent ?? 5,
    rep_name: agreement.rep_name || "Mr Salih Yaris",
    rep_position: agreement.rep_position || "",
    customer_name: agreement.customer_name || "",
    customer_address: agreement.customer_address || "",
    customer_email: agreement.customer_email || "",
    customer_phone: agreement.customer_phone || "",
    joint_applicant_name: agreement.joint_applicant_name || "",
  }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Coerce numerics
      ["property_price", "deposit_amount", "service_fee_percent"].forEach((k) => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      await axios.put(`${API}/admin/service-agreements/${agreement.id}`, payload, authHeaders());
      toast.success("Draft saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const borrowed = useMemo(() => {
    const p = Number(form.property_price || 0);
    const d = Number(form.deposit_amount || 0);
    return Math.max(p - d, 0);
  }, [form.property_price, form.deposit_amount]);
  const fee = useMemo(() => borrowed * (Number(form.service_fee_percent || 0) / 100), [borrowed, form.service_fee_percent]);

  return (
    <div className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-cream border border-forest/15 rounded-xl max-w-2xl w-full p-6 sm:p-8 my-6" data-testid="agreement-editor-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-serif text-xl text-forest">Edit Service Agreement draft</h3>
            <p className="text-[12.5px] text-sage mt-1">Yellow-marked boxes are the ones you should check before sending.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-forest/60 hover:text-forest"><X size={18} /></button>
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Customer name">
              <input type="text" value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))}
                className="w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
            </Field>
            <Field label="Joint applicant" hint="Leave blank for single-applicant">
              <input type="text" value={form.joint_applicant_name} onChange={(e) => setForm((f) => ({ ...f, joint_applicant_name: e.target.value }))}
                className="w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
            </Field>
          </div>
          <Field label="Full address">
            <textarea rows={2} value={form.customer_address} onChange={(e) => setForm((f) => ({ ...f, customer_address: e.target.value }))}
              className="w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Email"><input type="email" value={form.customer_email} onChange={(e) => setForm((f) => ({ ...f, customer_email: e.target.value }))} className="w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" /></Field>
            <Field label="Telephone"><input type="tel" value={form.customer_phone} onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))} className="w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" /></Field>
          </div>

          <hr className="border-forest/10" />

          <Field label="Agreed services, deliverables and exclusions" mark={!form.services_description.trim()}>
            <textarea rows={4} value={form.services_description} onChange={(e) => setForm((f) => ({ ...f, services_description: e.target.value }))}
              placeholder="Example: Advise and process a residential mortgage application including sourcing, submission and liaising with the lender until Mortgage Offer. Excludes legal conveyancing."
              className="w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Property price (£)" mark={!form.property_price}>
              <input type="number" step="0.01" value={form.property_price} onChange={(e) => setForm((f) => ({ ...f, property_price: e.target.value }))} className="w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
            </Field>
            <Field label="Deposit (£)" mark={!form.deposit_amount}>
              <input type="number" step="0.01" value={form.deposit_amount} onChange={(e) => setForm((f) => ({ ...f, deposit_amount: e.target.value }))} className="w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
            </Field>
            <Field label="Service fee (%)">
              <input type="number" step="0.1" value={form.service_fee_percent} onChange={(e) => setForm((f) => ({ ...f, service_fee_percent: e.target.value }))} className="w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
            </Field>
          </div>
          <p className="text-[12px] text-sage">
            Preview: Mortgage borrowing <strong className="text-forest">{fmtGBP(borrowed)}</strong> · Service fee <strong className="text-forest">{fmtGBP(fee)}</strong>
          </p>

          <hr className="border-forest/10" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Elin's representative name"><input type="text" value={form.rep_name} onChange={(e) => setForm((f) => ({ ...f, rep_name: e.target.value }))} className="w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" /></Field>
            <Field label="Position (optional)"><input type="text" value={form.rep_position} onChange={(e) => setForm((f) => ({ ...f, rep_position: e.target.value }))} className="w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" /></Field>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose} className="btn-ghost-dark">Cancel</button>
            <button type="button" onClick={save} disabled={saving} data-testid="agreement-editor-save" className="btn-gold disabled:opacity-50">{saving ? "Saving…" : "Save draft"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AgreementSignNow = ({ agreement, customer, onClose, onSigned }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [sig1, setSig1] = useState("");
  const [sig2, setSig2] = useState("");
  const [sigElin, setSigElin] = useState("");
  const sig1Ref = useRef(null);
  const sig2Ref = useRef(null);
  const sigElinRef = useRef(null);
  const [signing, setSigning] = useState(false);
  const [failure, setFailure] = useState(null); // {message} when the previous attempt failed

  const isJoint = Boolean((agreement.joint_applicant_name || "").trim());

  const submit = async () => {
    setFailure(null);
    if (!confirmed) { setFailure({ message: "Please tick the confirmation above before signing." }); return; }
    const s1 = sig1 || sig1Ref.current?.toDataURL();
    const s2 = isJoint ? (sig2 || sig2Ref.current?.toDataURL()) : null;
    const sE = sigElin || sigElinRef.current?.toDataURL();
    if (!s1 || s1.length < 1000) { setFailure({ message: "The customer has not signed yet. Please have them draw their signature in the box." }); return; }
    if (isJoint && (!s2 || s2.length < 1000)) { setFailure({ message: "The joint applicant has not signed yet. Both applicants need to sign." }); return; }
    setSigning(true);
    try {
      // No longer builds / sends an HTML snapshot — the server now builds its
      // own agreement HTML from the stored doc. This cuts the request body
      // from ~1 MB (large signatures + inline HTML) to just the three small
      // signature PNGs, which is why the "invalid response" / timeout
      // errors on live have stopped occurring here.
      const resp = await axios.post(
        `${API}/admin/service-agreements/${agreement.id}/sign-now`,
        {
          confirmed: true,
          signature_customer_1: s1,
          signature_customer_2: s2 || null,
          signature_elin: sE || null,
        },
        authHeaders()
      );
      if (resp.data?.already_signed) {
        toast.success("Agreement was already signed and filed.");
      } else {
        toast.success("Agreement signed and filed under Documents.");
      }
      onSigned();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "The signed agreement could not be filed. Please try again — your signatures are kept.";
      // Keep the modal open with the signatures still drawn, so the admin
      // can tap Retry without asking the client to sign again.
      setFailure({ message: msg });
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-cream border border-forest/15 rounded-xl max-w-3xl w-full p-6 sm:p-8 my-6" data-testid="agreement-sign-now-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-serif text-xl text-forest">Sign the agreement with the client</h3>
            <p className="text-[12.5px] text-sage mt-1">Open this on your laptop or tablet and have the client draw their signature.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-forest/60 hover:text-forest"><X size={18} /></button>
        </div>

        <AgreementReadonlyPreview a={agreement} />

        <label className="flex items-start gap-3 mt-4 cursor-pointer">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
            data-testid="agreement-signnow-confirm"
            className="mt-1 w-4 h-4 accent-gold" />
          <span className="text-[13px] text-forest">
            The client has read and understood this Service Agreement and agrees to it. By signing below they confirm their agreement.
          </span>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
          <div>
            <p className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70 mb-2">
              {isJoint ? "First applicant signature" : "Customer signature"} — {agreement.customer_name}
            </p>
            <SignaturePad ref={sig1Ref} value={sig1} onEnd={setSig1} testid="agreement-sig-customer" />
          </div>
          {isJoint && (
            <div>
              <p className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70 mb-2">
                Second applicant signature — {agreement.joint_applicant_name}
              </p>
              <SignaturePad ref={sig2Ref} value={sig2} onEnd={setSig2} testid="agreement-sig-customer-2" />
            </div>
          )}
          <div className={isJoint ? "md:col-span-2" : ""}>
            <p className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70 mb-2">
              For Elin's Consultancy — {agreement.rep_name}
            </p>
            <SignaturePad ref={sigElinRef} value={sigElin} onEnd={setSigElin} testid="agreement-sig-elin" />
          </div>
        </div>

        {failure && (
          <div
            data-testid="agreement-signnow-error"
            className="mt-5 bg-red-50 border border-red-200 rounded px-4 py-3 flex items-start gap-3"
          >
            <div className="flex-1">
              <p className="font-serif text-[14px] text-red-800">We couldn't file the signed agreement</p>
              <p className="text-[12.5px] text-red-700 mt-1">{failure.message}</p>
              <p className="text-[11.5px] text-red-700/80 mt-1">Your signatures are still drawn above. Tap Retry to try again — nothing was lost.</p>
            </div>
            <button
              type="button"
              onClick={submit}
              disabled={signing}
              data-testid="agreement-signnow-retry"
              className="btn-gold inline-flex items-center gap-1.5 !py-1.5 !px-3 disabled:opacity-50"
            >
              <RefreshCcw size={13} /> {signing ? "Retrying…" : "Retry"}
            </button>
          </div>
        )}

        <div className="flex gap-3 justify-end pt-5">
          <button type="button" onClick={onClose} className="btn-ghost-dark">Cancel</button>
          <button type="button" onClick={submit} disabled={signing || !confirmed} data-testid="agreement-signnow-submit" className="btn-gold disabled:opacity-50">{signing ? "Signing…" : "Sign and file"}</button>
        </div>
      </div>
    </div>
  );
};

const AgreementReadonlyPreview = ({ a }) => (
  <div className="bg-white border border-forest/10 rounded px-4 py-3 text-[13px] text-forest/80 space-y-1" data-testid="agreement-preview-block">
    <p><strong className="text-forest">Agreement date:</strong> {a.agreement_date_dmy}</p>
    <p><strong className="text-forest">Customer:</strong> {a.customer_name}{a.joint_applicant_name ? ` & ${a.joint_applicant_name}` : ""}</p>
    <p><strong className="text-forest">Address:</strong> {a.customer_address || "—"}</p>
    <p><strong className="text-forest">Service fee:</strong> {a.service_fee_percent}% of amount borrowed</p>
    {a.property_price ? <p><strong className="text-forest">Property price / deposit:</strong> {fmtGBP(a.property_price)} / {fmtGBP(a.deposit_amount)}</p> : null}
  </div>
);

const AgreementEmailModal = ({ agreement, onClose, onSent }) => {
  const [to, setTo] = useState(agreement.customer_email || "");
  const [sending, setSending] = useState(false);
  const send = async () => {
    const addr = (to || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) {
      toast.error("Enter a valid email address");
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/service-agreements/${agreement.id}/send-email`,
        { to_email: addr, link_base_url: window.location.origin },
        authHeaders()
      );
      toast.success(`Private link emailed to ${addr}`);
      onSent();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send email");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm flex items-center justify-center p-5">
      <div className="bg-white border border-forest/15 rounded-xl max-w-lg w-full p-6" data-testid="agreement-email-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-forest">Email the private link to {agreement.customer_name}</h3>
          <button type="button" onClick={onClose} className="p-1 text-forest/60 hover:text-forest"><X size={16} /></button>
        </div>
        <p className="text-[13px] text-sage mb-4">A short, branded cover note will be sent with a one-tap "Read &amp; sign your agreement" button. Only this agreement can be opened from the link.</p>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Send to</span>
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="agreement-email-to"
            placeholder="client@example.com"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none"
          />
        </label>
        <div className="flex gap-3 justify-end mt-5">
          <button type="button" onClick={onClose} className="btn-ghost-dark">Cancel</button>
          <button
            type="button"
            onClick={send}
            disabled={sending}
            data-testid="agreement-email-send"
            className="btn-gold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Send size={13} /> {sending ? "Sending…" : "Send email"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AgreementLinkModal = ({ agreement, onClose }) => {
  const url = `${window.location.origin}/agreement/${agreement.token}`;
  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };
  return (
    <div className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm flex items-center justify-center p-5">
      <div className="bg-white border border-forest/15 rounded-xl max-w-lg w-full p-6" data-testid="agreement-link-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-forest">Private link for the client</h3>
          <button type="button" onClick={onClose} className="p-1 text-forest/60 hover:text-forest"><X size={16} /></button>
        </div>
        <p className="text-[13px] text-sage">Share this with {agreement.customer_name}. Only they can open and sign this agreement — nothing else on the back office is visible.</p>
        <div className="flex items-stretch gap-2 mt-4">
          <input type="text" readOnly value={url} data-testid="agreement-link-url" className="flex-1 bg-cream-3 border border-forest/15 rounded px-3 py-2 text-[12.5px] font-mono text-forest" />
          <button type="button" onClick={copy} data-testid="agreement-link-copy" className="btn-gold inline-flex items-center gap-1.5"><Copy size={13} /> Copy</button>
        </div>
        <div className="flex justify-end mt-5">
          <button type="button" onClick={onClose} className="btn-ghost-dark">Close</button>
        </div>
      </div>
    </div>
  );
};
